import { useReducer } from 'react';
import { initialConversationState } from '../utils/conversationFlow';

const SESSION_ID_RANDOM_MAX = 1000;

const createInitialState = () => ({
  messages: [],
  conversationState: initialConversationState,
  flowStep: 1,
  sessionId: `session_${Date.now()}${Math.floor(Math.random() * SESSION_ID_RANDOM_MAX)}`,
  interestCategories: [],
  interestCategoriesLoading: false,
  amenityPlaces: [],
  pendingPlaces: [],
  pendingPlacesInterests: '',
  isFormSubmitted: false,
  additionalInterests: '',
  isAiMode: false,
  isAiThinking: false,
  aiThinkingWithLabel: false,
  isClosureFlowActive: false,
  isSessionEnded: false,
  hasShownFollowUp: false,
});

const tourGuideReducer = (state, action) => {
  switch (action.type) {
    case 'SET_MESSAGES': {
      // Preserve debug delay tag across rebuilds: callers often re-construct
      // messages from a locally-captured array that predates UPDATE_LAST_MESSAGE.
      const existing = new Map(state.messages.map((m) => [m.id, m]));
      const merged = action.payload.map((m) => {
        const prev = existing.get(m.id);
        if (prev && prev.nextDelayMs && !m.nextDelayMs) {
          return {
            ...m,
            nextDelayMs: prev.nextDelayMs,
            nextDelayDeadline: prev.nextDelayDeadline,
          };
        }
        return m;
      });
      // When a brand-new follow-up prompt enters the list (e.g. AI text
      // response with inlined next_step buttons), clear selectedFollowUpAction
      // so its buttons don't render as carried-over selections from the
      // previous turn. Mirrors the APPEND_MESSAGES reset.
      const hasNewFollowUpPrompt = action.payload.some(
        (m) => (m.stepType === 'next_step_prompt' || m.stepType === 'more_places_prompt')
          && !existing.has(m.id),
      );
      const conversationState = hasNewFollowUpPrompt
        ? { ...state.conversationState, selectedFollowUpAction: null }
        : state.conversationState;
      return { ...state, messages: merged, conversationState };
    }

    case 'APPEND_MESSAGES': {
      const tagged = action.deferred
        ? action.payload.map((m) => ({ ...m, deferred: true }))
        : action.payload;
      const newMessages = [...state.messages, ...tagged];
      const hasFollowUpPrompt = action.payload.some(
        (m) => m.stepType === 'next_step_prompt' || m.stepType === 'more_places_prompt',
      );
      const updatedConvState = {
        ...state.conversationState,
        visitedExternalLocations: [],
        ...(hasFollowUpPrompt && { selectedFollowUpAction: null }),
      };
      return { ...state, messages: newMessages, conversationState: updatedConvState };
    }

    case 'UPDATE_LAST_MESSAGE':
      if (state.messages.length === 0) return state;
      return {
        ...state,
        messages: state.messages.map((msg, i) => {
          const isLastMsg = i === state.messages.length - 1;
          return isLastMsg ? { ...msg, ...action.payload } : msg;
        }),
      };

    case 'SET_CONVERSATION_STATE':
      return {
        ...state,
        conversationState: typeof action.payload === 'function'
          ? action.payload(state.conversationState)
          : { ...state.conversationState, ...action.payload },
      };

    case 'REPLACE_CONVERSATION_STATE':
      return { ...state, conversationState: action.payload };

    case 'SET_FLOW_STEP':
      return { ...state, flowStep: action.payload };

    case 'SET_IS_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.payload };

    case 'SET_INTEREST_CATEGORIES':
      return { ...state, interestCategories: action.payload, interestCategoriesLoading: false };

    case 'SET_INTEREST_CATEGORIES_LOADING':
      return { ...state, interestCategoriesLoading: action.payload };

    case 'SET_AMENITY_PLACES':
      return { ...state, amenityPlaces: action.payload };

    case 'APPEND_AMENITY_PLACES': {
      const existingIds = state.amenityPlaces.map((p) => p.id);
      const newPlaces = action.payload.filter((p) => !existingIds.includes(p.id));
      return { ...state, amenityPlaces: [...state.amenityPlaces, ...newPlaces] };
    }

    case 'SET_PENDING_PLACES':
      return {
        ...state,
        pendingPlaces: action.payload.places,
        pendingPlacesInterests: action.payload.interests || state.pendingPlacesInterests,
      };

    case 'CLEAR_PENDING_PLACES':
      return { ...state, pendingPlaces: [], pendingPlacesInterests: '' };

    case 'SET_IS_FORM_SUBMITTED':
      return { ...state, isFormSubmitted: action.payload };

    case 'SET_ADDITIONAL_INTERESTS':
      return { ...state, additionalInterests: action.payload };

    case 'SET_IS_AI_MODE':
      return { ...state, isAiMode: action.payload };

    case 'SET_IS_AI_THINKING': {
      const p = action.payload;
      if (typeof p === 'object' && p !== null) {
        return { ...state, isAiThinking: !!p.active, aiThinkingWithLabel: !!p.withLabel };
      }
      return { ...state, isAiThinking: !!p, aiThinkingWithLabel: false };
    }

    case 'SET_IS_CLOSURE_FLOW_ACTIVE':
      return { ...state, isClosureFlowActive: action.payload };

    case 'SET_IS_SESSION_ENDED':
      return { ...state, isSessionEnded: action.payload };

    case 'SET_HAS_SHOWN_FOLLOW_UP':
      return { ...state, hasShownFollowUp: action.payload };

    case 'END_SESSION':
      return {
        ...state,
        messages: [...state.messages, ...action.payload],
        isSessionEnded: true,
        isClosureFlowActive: false,
      };

    case 'RESET_SESSION':
      return {
        ...state,
        sessionId: `session_${Date.now()}${Math.floor(Math.random() * SESSION_ID_RANDOM_MAX)}`,
        messages: [],
        conversationState: {
          currentStep: 'welcome_form',
          selectedInterests: [],
          selectedAmenities: [],
          visitedAmenities: [],
          userInfo: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            previousSession: false,
          },
          conversationData: {},
          formData: {},
          formErrors: {},
        },
        interestCategories: state.interestCategories,
        interestCategoriesLoading: false,
        amenityPlaces: [],
        pendingPlaces: [],
        pendingPlacesInterests: '',
        isFormSubmitted: false,
        additionalInterests: '',
        isAiMode: false,
        isAiThinking: false,
        aiThinkingWithLabel: false,
        isClosureFlowActive: false,
        isSessionEnded: false,
        flowStep: 1,
        isLoading: false,
        hasShownFollowUp: false,
      };

    default:
      return state;
  }
};

const useTourGuideState = () => {
  const [state, dispatch] = useReducer(tourGuideReducer, null, createInitialState);
  return { state, dispatch };
};

export default useTourGuideState;
